import React, { useEffect, useState, useCallback } from "react";
import { FieldArray, useFormikContext, Field } from "formik";
import { useDispatch } from "react-redux";
import { RolesListAction } from "../../redux/actions/general-action";
import { getUserByRoleIdAction } from "../../redux/actions/approval-work-flow-action";
import { Trash2 } from "lucide-react";
import LoadingOverlay from "../../components/helper-components/overlay-loading";
import errorToast from "../../components/helper-components/error-toast";
import MultiSelectChips from "../../components/forms/multi-chip-select";
import SearchField from "../../components/forms/search-field";
import { departmentListAction } from "../../redux/actions/settings-action";
import Select from "react-select";

const deptAlias = (i) => `originators_${i}_departmentObj`;
const roleAlias = (i) => `originators_${i}_roleObj`;
const sameOpt = (a, b) =>
  (a?.value ?? null) === (b?.value ?? null) &&
  (a?.label ?? null) === (b?.label ?? null);

export default function OriginatorTable() {
  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();

  // keep aliases in sync with nested fields so prefilled data shows
  useEffect(() => {
    const rows = values?.originators || [];
    rows.forEach((row, i) => {
      const desiredDept = row?.departmentObj ?? null;
      const aliasDept = values?.[deptAlias(i)] ?? null;
      if (!sameOpt(aliasDept, desiredDept)) {
        setFieldValue(deptAlias(i), desiredDept, false);
      }

      const desiredRole = row?.roleObj ?? null;
      const aliasRole = values?.[roleAlias(i)] ?? null;
      if (!sameOpt(aliasRole, desiredRole)) {
        setFieldValue(roleAlias(i), desiredRole, false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.originators]);

  // -------- Departments & Roles (fetch once) --------
  const [deptOptions, setDeptOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      departmentListAction(
        {},
        (data) => {
          setIsLoading(false);
          const rows = data?.data || [];
          const options = rows.map((dept) => ({
            value: dept.department_id,
            label: dept.department_name,
          }));
          console.log(options);
          setDeptOptions(options);
        },
        (err) => {
          setIsLoading(false);
          setDeptOptions([]);
          errorToast(err?.message || "Failed to load departments");
        }
      )
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      RolesListAction(
        (data) => {
          setIsLoading(false);
          const rows = data?.data || [];
          const opts = rows.map((r) => ({
            value: r.role_id,
            label: r.role_name,
          }));
          setRoleOptions(opts);
        },
        () => {
          setIsLoading(false);
          setRoleOptions([]);
        }
      )
    );
  }, [dispatch]);

  const loadDepartmentOptions = useCallback(
    async (input = "") => {
      const q = (input || "").toLowerCase().trim();
      const source = Array.isArray(deptOptions) ? deptOptions : [];
      const filtered = q
        ? source.filter((o) => (o.label || "").toLowerCase().includes(q))
        : source;
      return Promise.resolve(filtered);
    },
    [deptOptions]
  );

  const loadRoleOptions = useCallback(
    async (input = "") => {
      const q = (input || "").toLowerCase().trim();
      const source = Array.isArray(roleOptions) ? roleOptions : [];
      const filtered = q
        ? source.filter((o) => (o.label || "").toLowerCase().includes(q))
        : source;
      return Promise.resolve(filtered);
    },
    [roleOptions]
  );

  // -------- Users (per-row) --------
  const [usersOptionsByRow, setUsersOptionsByRow] = useState({}); // idx -> [{value,label}]
  const [rowLoading, setRowLoading] = useState({}); // idx -> boolean

  const setRowLoad = (i, v) =>
    setRowLoading((prev) => ({ ...prev, [i]: !!v }));

  const setRowUsers = (i, options) =>
    setUsersOptionsByRow((prev) => ({
      ...prev,
      [i]: Array.isArray(options) ? options : [],
    }));

  const fetchUsersForRow = useCallback(
    (i, roleId) => {
      if (!roleId) {
        setRowUsers(i, []);
        return;
      }
      setRowLoad(i, true);
      dispatch(
        getUserByRoleIdAction(
          { role_id: roleId },
          (data) => {
            const rows = data?.data || [];
            const opts = rows.map((u) => ({
              value: u.user_id,
              label: u.user_name,
            }));
            setRowUsers(i, opts);
            setRowLoad(i, false);
          },
          (err) => {
            setRowUsers(i, []);
            setRowLoad(i, false);
            errorToast(err?.message || "Failed to load users");
          }
        )
      );
    },
    [dispatch]
  );

  // prefetch users for rows that already have a role (edit mode)
  useEffect(() => {
    (values?.originators || []).forEach((row, i) => {
      const roleId = row?.role || row?.roleObj?.value;
      if (roleId && !usersOptionsByRow[i]) {
        fetchUsersForRow(i, roleId);
      }
    });
  }, [values?.originators, usersOptionsByRow, fetchUsersForRow]);

  return (
    <FieldArray
      name="originators"
      render={({ push, remove }) => (
        <div className="w-full">
          <LoadingOverlay isLoading={isLoading} />

          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={() =>
                push({
                  department: "",
                  departmentObj: null,
                  role: "",
                  roleObj: null,
                  users: [],
                })
              }
              className="bg-primary text-white px-3 py-2 rounded-md text-sm shadow-sm hover:opacity-95"
            >
              + Add Originator
            </button>
          </div>

          <div className="max-h-[320px] w-auto height-auto overflow-visible">
            <div className="relative h-auto w-auto rounded-md border border-[var(--button-border-color)] bg-white bg-clip-padding">
              <div className="h-auto w-auto overflow-visible border rounded-md outline outline-[var(--list-table-border-color)] outline-1 border-radius">
                <table className="w-full text-sm text-black border-separate border-spacing-0">
                  <thead className="bg-[var(--list-table-heading-bg)] text-white border rounded-md">
                    <tr className="[&>th]:text-left [&>th]:font-medium [&>th]:text-gray-600">
                      <th className="px-4 py-3 w-[24%]">DEPARTMENT</th>
                      <th className="px-4 py-3 w-[24%]">ROLE</th>
                      <th className="px-4 py-3">USERS</th>
                      <th className="px-4 py-3 w-[80px]">ACTION</th>
                    </tr>
                  </thead>

                  <tbody className="bg-white">
                    {(values.originators || []).map((row, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        {/* DEPARTMENT */}
                        <td className="px-4 py-3 align-top">
                          <div className="relative">
                            {/* <SearchField
                            name={deptAlias(idx)}
                            isLabelReq={false}
                            placeholder="Select department"
                            type="text"
                            isValidNewOption={false}
                            showCreateCTA={false}
                            noOptionsText="No departments found"
                            className="w-full"
                            loadOptions={loadDepartmentOptions}
                            onChange={(opt, meta) => {
                              setFieldValue(deptAlias(idx), opt || null);
                              setFieldValue(`originators[${idx}].departmentObj`, opt || null);
                              setFieldValue(`originators[${idx}].department`, opt?.value || "");
                              if (meta?.action === "clear") {
                                setFieldValue(deptAlias(idx), null);
                              }
                            }}
                          /> */}
                            <Field name={deptAlias(idx)}>
                              {({ field, form }) => {
                                const { setFieldValue, setFieldTouched } = form;
                                const selectedOption =
                                  field.value ||
                                  deptOptions.find(
                                    (opt) =>
                                      opt.value === row?.department
                                  ) ||
                                  null;

                                return (
                                  <Select
                                    inputId={`department-${idx}`}
                                    options={deptOptions}
                                    value={selectedOption}
                                    onChange={(option) => {
                                      const val = option?.value || "";
                                      setFieldValue(
                                        deptAlias(idx),
                                        option || null
                                      );
                                      setFieldValue(
                                        `originators[${idx}].departmentObj`,
                                        option || null
                                      );
                                      setFieldValue(
                                        `originators[${idx}].department`,
                                        val || ""
                                      );
                                    }}
                                    onBlur={() =>
                                      setFieldTouched(deptAlias(idx), true)
                                    }
                                    components={{
                                      IndicatorSeparator: () => null,
                                    }}
                                    placeholder="Select Department"
                                    isSearchable={true}
                                    maxMenuHeight={5 * 40} // ~5 options visible
                                    classNamePrefix="doc-cat-select"
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "36px",
                                        borderColor:
                                          "var(--toggle-switch-off-bg)",
                                        boxShadow: "none",
                                        "&:hover": {
                                          borderColor:
                                            "var(--toggle-switch-off-bg)",
                                        },
                                      }),
                                      menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                  />
                                );
                              }}
                            </Field>
                          </div>
                        </td>

                        {/* ROLE */}
                        <td className="px-4 py-3 align-top">
                          <div className="relative">
                            {/* <SearchField
                              name={roleAlias(idx)}
                              isLabelReq={false}
                              placeholder="Select role"
                              type="text"
                              isValidNewOption={false}
                              showCreateCTA={false}
                              noOptionsText="No roles found"
                              className="w-full"
                              loadOptions={loadRoleOptions}
                              onChange={(opt, meta) => {
                                setFieldValue(roleAlias(idx), opt || null);
                                setFieldValue(
                                  `originators[${idx}].roleObj`,
                                  opt || null
                                );
                                setFieldValue(
                                  `originators[${idx}].role`,
                                  opt?.value || ""
                                );

                                // reset users when role changes
                                setFieldValue(
                                  `originators[${idx}].users`,
                                  []
                                );
                                setRowUsers(idx, []);

                                if (
                                  meta?.action !== "clear" &&
                                  opt?.value
                                ) {
                                  // fetch users for this role (per row)
                                  fetchUsersForRow(idx, opt.value);
                                }
                              }}
                            /> */}
                            <Field name={roleAlias(idx)}>
                              {({ field, form }) => {
                                const { setFieldValue, setFieldTouched } = form;
                                const selectedOption =
                                  field.value ||
                                  roleOptions.find((opt) => opt.value === row?.role) ||
                                  null;

                                return (
                                  <Select
                                    inputId={`role-${idx}`}
                                    options={roleOptions}
                                    value={selectedOption}
                                    onChange={(option, meta) => {
                                      const val = option?.value || "";
                                      setFieldValue(roleAlias(idx), option || null);
                                      setFieldValue(
                                        `originators[${idx}].roleObj`,
                                        option || null
                                      );
                                      setFieldValue(
                                        `originators[${idx}].role`,
                                        val || ""
                                      );
                                      setFieldValue(`originators[${idx}].users`, []);
                                      setRowUsers(idx, []);
                                      if (meta?.action !== "clear" && val) {
                                        fetchUsersForRow(idx, val);
                                      }
                                    }}
                                    onBlur={() => setFieldTouched(roleAlias(idx), true)}
                                    components={{
                                      IndicatorSeparator: () => null,
                                    }}
                                    placeholder="Select Role"
                                    isSearchable={true}
                                    maxMenuHeight={5 * 40}
                                    classNamePrefix="role-select"
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "36px",
                                        borderColor: "var(--toggle-switch-off-bg)",
                                        boxShadow: "none",
                                        "&:hover": {
                                          borderColor: "var(--toggle-switch-off-bg)",
                                        },
                                      }),
                                      menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                  />
                                );
                              }}
                            </Field>
                          </div>
                        </td>

                        {/* USERS */}
                        <td className="px-4 py-3 align-top">
                          <MultiSelectChips
                            name={`originators[${idx}].users`}
                            value={row.users || []}
                            onChange={(selectedIds) => {
                              setFieldValue(
                                `originators[${idx}].users`,
                                selectedIds
                              );
                              const userNames = (usersOptionsByRow[idx] || [])
                                .filter((opt) =>
                                  selectedIds.includes(opt.value)
                                )
                                .map((opt) => opt.label);
                              setFieldValue(
                                `originators[${idx}].user_ids`,
                                selectedIds
                              );
                              setFieldValue(
                                `originators[${idx}].user_names`,
                                userNames
                              );
                            }}
                            options={usersOptionsByRow[idx] || []}
                            placeholder={
                              rowLoading[idx]
                                ? "Loading users..."
                                : "Search users..."
                            }
                            disabled={!!rowLoading[idx]}
                          />
                        </td>

                        {/* ACTION */}
                        <td className="px-4 py-3 align-top">
                          <button
                            type="button"
                            onClick={() => {
                              setFieldValue(deptAlias(idx), null, false);
                              setFieldValue(roleAlias(idx), null, false);
                              remove(idx);
                            }}
                            className="p-2 rounded-md hover:bg-[var(--remove-color)] hover:text-white text-[var(--remove-color)]"
                            aria-label="Delete row"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {(!values.originators ||
                      values.originators.length === 0) && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-8 text-center text-gray-400"
                        >
                          No originators added yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    />
  );
}
